import React, { useEffect, useState } from 'react';
import { Download, X, Share, Plus } from 'lucide-react';

/**
 * PWA install prompt.
 * - Android/Chrome/Edge: uses native `beforeinstallprompt` event
 * - iOS Safari: shows manual "Share → Add to Home Screen" instructions
 */
export default function InstallPwaPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIos, setIsIos] = useState(false);

  useEffect(() => {
    const ua = window.navigator.userAgent || '';
    const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    // eslint-disable-next-line no-undef
    const inStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;

    const dismissedAt = Number(localStorage.getItem('pwa-install-dismissed') || 0);
    const recentlyDismissed = Date.now() - dismissedAt < 7 * 24 * 60 * 60 * 1000;

    // Listen for manual re-trigger from sidebar Install button
    const onManualShow = () => {
      if (inStandalone) return;
      setVisible(true);
    };
    window.addEventListener('evabrew-show-install-prompt', onManualShow);

    if (inStandalone) {
      return () => window.removeEventListener('evabrew-show-install-prompt', onManualShow);
    }

    if (iOS) {
      setIsIos(true);
      if (!recentlyDismissed) setVisible(true);
      return () => window.removeEventListener('evabrew-show-install-prompt', onManualShow);
    }

    // Non-iOS: wait for native prompt
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      if (!recentlyDismissed) setVisible(true);
    };
    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
      window.removeEventListener('evabrew-show-install-prompt', onManualShow);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === 'dismissed') {
      localStorage.setItem('pwa-install-dismissed', String(Date.now()));
    }
    setDeferred(null);
    setVisible(false);
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa-install-dismissed', String(Date.now()));
    setVisible(false);
  };

  if (!visible) return null;

  // ----- iOS Variant -----
  if (isIos) {
    return (
      <div
        data-testid="pwa-install-prompt-ios"
        className="fixed bottom-4 left-4 right-4 z-50 max-w-md mx-auto bg-white border border-amber-200 rounded-xl shadow-2xl p-4"
      >
        <div className="flex items-start gap-3">
          <div className="bg-gradient-to-br from-amber-500 to-orange-600 text-white rounded-lg p-2 flex-shrink-0">
            <Download className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-800">Install Evabrew on your iPhone</p>
            <ol className="mt-2 space-y-1.5 text-sm text-gray-700">
              <li className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex-shrink-0">1</span>
                <span>Tap the <Share className="inline w-4 h-4 text-blue-600" /> <strong>Share</strong> icon at the bottom of Safari</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex-shrink-0">2</span>
                <span>Scroll and tap <Plus className="inline w-4 h-4" /> <strong>Add to Home Screen</strong></span>
              </li>
              <li className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex-shrink-0">3</span>
                <span>Tap <strong>Add</strong> — you're done!</span>
              </li>
            </ol>
            <p className="text-xs text-gray-500 mt-2">Works only in Safari, not Chrome on iPhone.</p>
          </div>
          <button
            onClick={handleDismiss}
            className="text-gray-400 hover:text-gray-600 flex-shrink-0"
            aria-label="Dismiss"
            data-testid="pwa-dismiss-ios"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // ----- Chromium Variant (Android/Desktop) -----
  if (!deferred) {
    // Manual show without deferred event — give generic guidance
    return (
      <div
        data-testid="pwa-install-prompt-generic"
        className="fixed bottom-6 left-6 z-50 max-w-sm bg-white border border-amber-200 rounded-xl shadow-2xl p-4 flex items-start gap-3"
      >
        <div className="bg-gradient-to-br from-amber-500 to-orange-600 text-white rounded-lg p-2">
          <Download className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <p className="font-semibold text-gray-800">Install Evabrew</p>
          <p className="text-xs text-gray-600 mt-1">
            Open your browser's menu (⋮) and look for <strong>Install app</strong> or <strong>Add to Home screen</strong>.
          </p>
        </div>
        <button
          onClick={handleDismiss}
          className="text-gray-400 hover:text-gray-600"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }
  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed bottom-6 left-6 z-50 max-w-sm bg-white border border-amber-200 rounded-xl shadow-2xl p-4 flex items-start gap-3"
    >
      <div className="bg-gradient-to-br from-amber-500 to-orange-600 text-white rounded-lg p-2">
        <Download className="w-5 h-5" />
      </div>
      <div className="flex-1">
        <p className="font-semibold text-gray-800">Install Evabrew</p>
        <p className="text-xs text-gray-600 mt-1">
          Add to your device for faster access and a full-screen experience.
        </p>
        <div className="flex gap-2 mt-3">
          <button
            onClick={handleInstall}
            data-testid="pwa-install-button"
            className="bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold px-3 py-1.5 rounded-lg transition"
          >
            Install
          </button>
          <button
            onClick={handleDismiss}
            className="text-gray-600 hover:text-gray-900 text-sm px-3 py-1.5"
          >
            Not now
          </button>
        </div>
      </div>
      <button
        onClick={handleDismiss}
        className="text-gray-400 hover:text-gray-600"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
